/**
 * Canonical AT-bridge identity derivation for ActivityPub-sourced accounts (#2226).
 *
 * Trust model: a hostile Mastodon/Pixelfed instance fully controls every field on the
 * `Account` object it serves (`acct`, `username`, `uri`, ...). This module never treats
 * any of those fields as authoritative on its own — it establishes ONE canonical identity
 * per account, computed from `acct` cross-checked against `username`, and that same value
 * feeds display, routing, and the verification-batch identifier.
 *
 * The acct/username cross-check is corroboration, not proof, and it is only meaningful
 * when the account JSON came from a party the user trusts to have done real
 * webfinger-based discovery of the remote account — the user's own authenticated
 * instance, or Bridgy Fed's own server (`bsky.brid.gy`), which is authoritative about the
 * accounts it bridges and is not the threat actor in this model. When account data is
 * instead fetched directly from the VIEWED account's own (or a post's own) instance —
 * e.g. a profile/follower/thread fetch with no session on that instance — that instance
 * controls both `acct` and `username` and can make them mutually consistent, so the
 * cross-check passes trivially while lying about the underlying identity. Callers on
 * that fetch path MUST compute `isTrustedOrigin` via `isTrustedBridgeOrigin` (below) and
 * pass it through to `deriveATBridgeFromAcct`. The parameter defaults to trusted, which
 * is only correct for callers that already route the fetch through a trusted party
 * (the common case — authenticated timeline/search/favourites fetches always go through
 * the user's own session instance); any call site that fetches account data directly
 * from an account's own instance must pass an explicit, computed value.
 *
 * When the origin is genuinely untrusted and correctly reported as such, the only
 * identity a hostile instance can invoke is an overt one visible in the UI (the raw,
 * unclean `handle@bsky.brid.gy`-suffixed acct) — never a borrowed DID or borrowed
 * verification checkmark.
 *
 * A DID is NEVER derived from remote-instance data here — not from `uri`, not from a
 * handle-less bridged form. DIDs only ever come from server-side handle resolution.
 */
import { cleanBridgyFedHandle, isBridgyFedHandle, isValidATHandle } from '@/lib/bridge'
import { canonicalizeInstanceOrigin } from '@/lib/mastodonFetch'
import type { PostAuthor } from '@/types/posts'

export type ATBridgeIdentity =
    | {
          isBridged: true
          /** Clean AT handle — used for display (handle/displayHandle) and routing. */
          handle: string
          /** Original `acct` value (e.g. `whiteside.io@bsky.brid.gy`) — carried in bridgeStatus. */
          activityPubHandle: string
          /** The identifier to send to the verification batch endpoint. */
          verificationIdentifier: string
      }
    | { isBridged: false }

/**
 * Derive the canonical AT-bridge identity from an AP account's `acct`, cross-checked
 * against `username`. Both fields are remote-instance-controlled; agreement between
 * them is not proof of authenticity, only a precondition for treating the account as
 * bridged at all — and only when `isTrustedOrigin` says the underlying fetch is
 * itself trustworthy (see module docblock). Any failure (untrusted origin, not a
 * Bridgy handle, empty prefix, invalid AT handle shape, or acct/username
 * disagreement) yields `{ isBridged: false }` — the mapper falls through to the
 * plain AP-native path.
 *
 * `username` is compared only when it was actually supplied (`!== undefined`) — an
 * empty string (`''`) is a real, attacker-controllable value distinct from "not
 * supplied", and must be treated as a disagreement, not skipped. The internal
 * single-arg call from `verificationIdentifierForAuthor` below relies on the
 * genuinely-absent (`undefined`) case still passing.
 */
export function deriveATBridgeFromAcct(
    acct: string,
    username?: string,
    isTrustedOrigin = true
): ATBridgeIdentity {
    if (!isTrustedOrigin) return { isBridged: false }
    if (!isBridgyFedHandle(acct)) return { isBridged: false }

    const handle = cleanBridgyFedHandle(acct)
    if (!handle) return { isBridged: false }
    if (!isValidATHandle(handle)) return { isBridged: false }
    if (username !== undefined && username.toLowerCase() !== handle.toLowerCase()) {
        return { isBridged: false }
    }

    return {
        isBridged: true,
        handle,
        activityPubHandle: acct,
        verificationIdentifier: acct,
    }
}

const BSKY_BRID_GY_HOSTNAME = 'bsky.brid.gy'

/**
 * Whether an AP account/status fetch's origin is trustworthy enough for
 * `deriveATBridgeFromAcct`'s acct/username cross-check to mean anything (see the
 * module docblock).
 *
 * Trusted when the fetch went to Bridgy Fed's own server (authoritative about the
 * accounts it bridges), or to the same canonical origin as the caller's own
 * authenticated session instance (real webfinger-based federation, not an arbitrary
 * instance's self-report about itself). A canonicalization failure on either input
 * (malformed/unsafe URL) fails closed — untrusted.
 */
export function isTrustedBridgeOrigin(
    fetchedInstanceUrl: string,
    sessionInstanceUrl?: string
): boolean {
    let fetchedOrigin: string
    try {
        fetchedOrigin = canonicalizeInstanceOrigin(fetchedInstanceUrl).origin
    } catch {
        return false
    }

    try {
        if (new URL(fetchedOrigin).hostname.toLowerCase() === BSKY_BRID_GY_HOSTNAME) return true
    } catch {
        return false
    }

    if (!sessionInstanceUrl) return false
    try {
        return canonicalizeInstanceOrigin(sessionInstanceUrl).origin === fetchedOrigin
    } catch {
        return false
    }
}

/**
 * Mirror of `VALID_DID_RE` in `xrpc-server/src/validation.ts:1`. Keep these in sync —
 * a mismatch here would let a malformed `did:`-shaped value from any PostAuthor
 * producer poison a batch verification request (xrpc-server rejects the entire
 * batch on one invalid identifier). The did:plc half of this grammar is also
 * synchronized with `PLC_DID_RE` in `packages/shared/src/did.ts` and mobile
 * `lib/did.ts` — this copy legitimately admits `did:web` too, for batch
 * verification identifiers that the plc-only copies reject.
 */
export const XRPC_VALID_DID_RE = /^did:(plc:[a-z2-7]{24}|web:[a-zA-Z0-9._:%-]+)$/

/**
 * Compute the identifier to use for batch verification lookups for a post author.
 *
 * AT-native authors pass through their DID, but only when it matches xrpc-server's
 * exact DID grammar — a malformed `did:`-shaped value never reaches the batch.
 * Bridged AT→AP authors resolve to an `X@bsky.brid.gy` identifier: xrpc-server's
 * `resolveMastodonHandleToDid` only accepts that suffixed form (a bare AT handle
 * returns null), and `X` must pass `AT_HANDLE_PATTERN` or xrpc-server rejects the
 * whole batch. Everything else (AP-native authors, unrecognized shapes) yields
 * `undefined` — no verification identifier, no signal.
 */
export function verificationIdentifierForAuthor(
    author?: Pick<PostAuthor, 'did' | 'handle' | 'bridgeStatus'>
): string | undefined {
    if (!author) return undefined

    if (author.did && XRPC_VALID_DID_RE.test(author.did)) {
        return author.did
    }

    if (author.bridgeStatus?.isBridged && author.bridgeStatus.sourceProtocol === 'atprotocol') {
        const apHandle = author.bridgeStatus.activityPubHandle
        if (apHandle && deriveATBridgeFromAcct(apHandle).isBridged) {
            return apHandle
        }
        if (author.handle && isValidATHandle(author.handle)) {
            return `${author.handle}@bsky.brid.gy`
        }
        return undefined
    }

    return undefined
}
